import { Box, Button } from "@mui/material";
import { atom, useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toBase64 } from "../../utils/base64";
import { toSlug } from "../../utils/create-slug";
import { useDialog } from "../../utils/hooks/use-dialog";
import { useAnalytics } from "../analytics/analytics";
import { imageUrlOverrideFamily } from "../multiformat/store/image-url-override-family";
import { previewsFamily } from "../multiformat/store/previews-family";
import { ContentPreviewImage } from "./content-preview-image";
import { UpdateImageryButton } from "./update-imagery-button";
import { UpdateImagerySidebar } from "./update-imagery-sidebar";

import type { CustomTemplates } from "../../pages/create-content/use-custom-templates";
import type { FontSelectionSummary } from "../brand-kit/hooks/use-font-selections";
import type { BrandTone } from "../brand-kit/server/brand-tone-service";
import type { AccountContext } from "../generic/hooks/use-account-context";
import type { ImageUrl } from "../multiformat/store/preview-family";
import type { MultiformatConfig, MultiformatLayout } from "../multiformat/store/types";
import type { FC } from "react";

export const ContentPreviews: FC<{
  multiformatConfig: MultiformatConfig;
  answer: string;
  useSelectedLogo: boolean;
  customTemplates: CustomTemplates | null;
  designer?: string | null;
  fontSelections: FontSelectionSummary[] | null;
  db: string | null;
  fileId?: string;
  imageStyles?: string[];
  brandTones: BrandTone[] | null;
  tone?: string;
  handlePageReady: () => void;
  holdingDialogOpen?: boolean;
  context?: AccountContext;
}> = ({
  multiformatConfig,
  answer,
  useSelectedLogo,
  customTemplates,
  designer,
  fontSelections,
  db,
  fileId,
  imageStyles,
  brandTones,
  tone,
  handlePageReady,
  holdingDialogOpen = false,
  context,
}) => {
  const navigate = useNavigate();
  const { gaEvent } = useAnalytics();
  const updateImageryDialog = useDialog();
  const [resetKey, setResetKey] = useState(0);
  const [hasImageSelection, setHasImageSelection] = useState(false);

  const previews = useAtomValue(
    previewsFamily({
      contentTypeName: multiformatConfig.name,
      answer,
      customTemplates,
      useSelectedLogo,
      fontSelections: fontSelections || [],
      db: db || undefined,
      fileId,
      imageStyles,
      brandTones: brandTones || [],
      tone,
      ...(context && { context }),
    }),
  );

  const formatId = toSlug(multiformatConfig.name).replace(/-/g, "_");

  const setImageUrl = useAtomCallback(
    useCallback(
      (get, set, index: number, urls: ImageUrl[] | null) => {
        const overrideAtom = imageUrlOverrideFamily({
          formatId,
          imgNum: index + 1,
          answer,
          fileId,
        });
        set(overrideAtom, urls);
      },
      [formatId, answer, fileId],
    ),
  );

  const itemsPerPage = 4;
  const numPreviews = previews?.length ?? 0;
  const firstPageSize = numPreviews > 0 ? Math.min(numPreviews, itemsPerPage) : itemsPerPage;

  const [numItems, setNumItems] = useState(firstPageSize);
  const [prevPage, setPrevPage] = useState({ answer, numPreviews });
  if (answer !== prevPage.answer || numPreviews !== prevPage.numPreviews) {
    setPrevPage({ answer, numPreviews });
    if (numPreviews > 0) {
      setNumItems(firstPageSize);
    }
  }

  // Check if image overrides already exist
  const hasAppliedToAll = useAtomValue(
    useMemo(() => {
      const hasOverridesAtom = atom((get) => {
        if (numPreviews === 0) return false;

        // Check if any preview has image overrides
        for (let i = 0; i < numPreviews; i++) {
          const overrideAtom = imageUrlOverrideFamily({
            formatId,
            imgNum: i + 1,
            answer,
            fileId,
          });
          const overrides = get(overrideAtom);
          if (overrides && overrides.length > 0) {
            return true;
          }
        }
        return false;
      });
      return hasOverridesAtom;
    }, [formatId, answer, fileId, numPreviews]),
  );

  const loadMore = useRef<HTMLButtonElement>(null);

  const handleLoadMore = useCallback(() => {
    gaEvent("multiformat_button", { action: "click", value: "Load More" });

    setNumItems((prev) => Math.min(prev + itemsPerPage, numPreviews));
    setTimeout(() => {
      loadMore.current?.scrollIntoView?.({ behavior: "smooth" });
    }, 200);
  }, [gaEvent, numPreviews]);

  const handleEdit = useCallback(
    (i: number) => {
      gaEvent("multiformat_button", {
        action: "click",
        value: `Edit Position ${i + 1}`,
      });

      if (previews?.[i]!.state === "hasData" && previews?.[i].data) {
        const { preview } = previews[i].data;
        const d = toBase64(
          JSON.stringify({
            contentType: multiformatConfig.name,
            customizations: preview.customizations,
            page: preview.page_number,
            templateCode: preview.template_code,
            customTemplate: previews[i].data.customTemplate,
            contentGenerationResultId: previews[i].data.contentGenerationResultId,
          }),
        );

        const params = new URLSearchParams({
          d,
          title: preview.title && preview.title !== "" ? preview.title : multiformatConfig.name,
        });
        void navigate({ pathname: "/create-content/edit", search: params.toString() });
      }
    },
    [gaEvent, previews, multiformatConfig, navigate],
  );

  const getLayoutForIndex = useCallback(
    (i: number): MultiformatLayout | undefined => {
      const layoutNumber = i % multiformatConfig.layouts.length;
      const layout = multiformatConfig.layouts[layoutNumber];

      return layout;
    },
    [multiformatConfig],
  );

  const hasInvalidImages = useCallback(
    (i: number): boolean => {
      const layout = getLayoutForIndex(i);
      if (!layout) return false;

      if (layout.images.length === 0) return true;

      return layout.images.some((image) => {
        if (image.type === "icon") {
          return true;
        }
        return false;
      });
    },
    [getLayoutForIndex],
  );

  const handleApplyExistingImageToAll = useCallback(
    (i: number) => {
      if (previews?.[i]!.state === "hasData" && previews?.[i].data) {
        try {
          const sourcePreview = previews[i].data.preview;
          const sourceCustomizations = sourcePreview.customizations;

          if (!sourceCustomizations?.classes) {
            console.warn("No customizations found in source preview");
            return;
          }

          const extractedImageUrls: ImageUrl[] = [];

          Object.entries(sourceCustomizations.classes).forEach(([tag, value]) => {
            if (typeof value !== "object" || value === null) return;

            if ("masked_media" in value) {
              extractedImageUrls.push({
                tag,
                type: "photo",
                url: value.masked_media!.url,
                index: 0,
              });
            } else if ("asset" in value) {
              extractedImageUrls.push({
                tag,
                type: "video",
                asset_id: value.asset!.asset_id!,
                index: 0,
              });
            }
          });

          // Apply the extracted imageUrls to all other previews
          previews.forEach((_, index) => {
            // Don't update text only previews
            const layout = getLayoutForIndex(index);

            if (layout?.images.length === 0) return;

            if (index !== i) {
              setImageUrl(index, extractedImageUrls);
            }
          });

          setHasImageSelection(true);
        } catch (error) {
          console.error("Error applying to all:", error);
        }
      }
    },
    [getLayoutForIndex, previews, setImageUrl],
  );

  const handleRevertToOriginal = useCallback(() => {
    const previewCount = previews?.length || 0;
    for (let i = 0; i < previewCount; i++) {
      setImageUrl(i, null);
    }
    setResetKey((prev) => prev + 1);
    setHasImageSelection(false);
  }, [setImageUrl, previews?.length]);

  const handleApplyImageUrlToAll = useCallback(
    (imageUrl: string) => {
      const previewCount = previews?.length || 0;
      const isMultipage = multiformatConfig.multipage ?? false;

      for (let i = 0; i < previewCount; i++) {
        const layout = getLayoutForIndex(i);
        if (layout?.images.length === 0) continue;

        let extractedImageUrls: ImageUrl[];

        if (isMultipage && layout?.images.length) {
          const existingImageUrls: ImageUrl[] = [];

          const preview = previews?.[i];
          if (preview?.state === "hasData" && preview.data) {
            const { customizations } = preview.data.preview;
            if (customizations?.classes) {
              Object.entries(customizations.classes).forEach(([tag, value]) => {
                if (typeof value !== "object" || value === null) return;
                // We don't want image1, only image2 -> imageN
                if ("masked_media" in value && tag !== "image1") {
                  existingImageUrls.push({
                    tag,
                    type: "photo",
                    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                    url: (value.masked_media as { url: string }).url,
                    index: 0,
                  });
                }
              });
            }
          }

          extractedImageUrls = [
            {
              tag: "image1",
              type: "photo" as const,
              url: imageUrl,
              index: 0,
            },
            ...existingImageUrls,
          ];
        } else {
          extractedImageUrls = [
            {
              tag: "photo",
              type: "photo",
              url: imageUrl,
              index: 0,
            },
          ];
        }

        setImageUrl(i, extractedImageUrls);
      }
    },
    [multiformatConfig, getLayoutForIndex, previews, setImageUrl],
  );

  const handleOnLoaded = useCallback(
    (index: number) => {
      if (index < 4) {
        handlePageReady();
      }
    },
    [handlePageReady],
  );

  return (
    <>
      {multiformatConfig.layouts.some((l) => l.images.some((i) => i.type)) && (
        <UpdateImageryButton
          onOpen={updateImageryDialog.handleOpen}
          onClose={updateImageryDialog.handleClose}
          onReset={handleRevertToOriginal}
          isOpen={updateImageryDialog.open}
          showReset={hasImageSelection}
          disabled={holdingDialogOpen}
        />
      )}
      <Box
        sx={[
          (theme) => ({
            display: "grid",
            gap: 2,
            [theme.breakpoints.down("xs")]: {
              gridTemplateColumns: "1fr",
            },
            [theme.breakpoints.up("sm")]: {
              gridTemplateColumns: "repeat(2, 1fr)",
            },
            [theme.breakpoints.up("lg")]: {
              gridTemplateColumns: "repeat(2, 1fr)",
            },
          }),
        ]}
      >
        {previews?.map((preview, i) =>
          preview === undefined ? null : (
            <ContentPreviewImage
              // oxlint-disable-next-line react/no-array-index-key -- Positional slots; the index is the identity
              key={i}
              image={preview}
              onEdit={() => handleEdit(i)}
              onApplyToAll={
                !hasInvalidImages(i) && !hasAppliedToAll
                  ? () => handleApplyExistingImageToAll(i)
                  : undefined
              }
              hidden={i >= numItems}
              designer={designer ?? null}
              dimensions={multiformatConfig.design_huddle!.dimensions!}
              contentTypeName={multiformatConfig.name}
              onLoaded={() => handleOnLoaded(i)}
            />
          ),
        )}
      </Box>
      {numItems < numPreviews && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <Button
            data-analytics-id="design-huddle-content-previews-load-more"
            onClick={handleLoadMore}
            ref={loadMore}
            variant="contained"
            color="primary"
            size="large"
            loading={false}
          >
            Load More
          </Button>
        </Box>
      )}
      <UpdateImagerySidebar
        open={updateImageryDialog.open}
        handleClose={updateImageryDialog.handleClose}
        onApplyToAll={handleApplyImageUrlToAll}
        onSelectionChange={setHasImageSelection}
        resetKey={resetKey}
      />
    </>
  );
};
